// ads.ts — AdMob for the native app (Capacitor). The web build shows no ads.
//
// Real unit ids come from VITE_ADMOB_* at build time (see .env.android.example);
// without them Google's official test units are used, which never pay out but
// are safe to click during development.
import { Capacitor } from "@capacitor/core";

const TEST = {
  interstitial: "ca-app-pub-3940256099942544/1033173712",
  rewarded: "ca-app-pub-3940256099942544/5224354917",
};

const IDS = {
  interstitial: import.meta.env.VITE_ADMOB_INTERSTITIAL || TEST.interstitial,
  rewarded: import.meta.env.VITE_ADMOB_REWARDED || TEST.rewarded,
};
const TESTING = IDS.interstitial === TEST.interstitial;

/** Rounds between interstitials and minimum seconds between two of them. */
const INTERSTITIAL_EVERY = 2;
const INTERSTITIAL_GAP_MS = 90_000;

let ready = false;
let roundsSinceAd = 0;
let lastInterstitial = 0;

type AdMobModule = typeof import("@capacitor-community/admob");
let admob: AdMobModule | undefined;

export function adsAvailable() {
  return ready;
}

export async function initAds() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    admob = await import("@capacitor-community/admob");
    const { AdMob, AdmobConsentStatus } = admob;
    await AdMob.initialize({ initializeForTesting: TESTING });
    // UMP consent (GDPR) — required before personalised ads in the EU/UK.
    const consent = await AdMob.requestConsentInfo();
    if (consent.isConsentFormAvailable && consent.status === AdmobConsentStatus.REQUIRED) {
      await AdMob.showConsentForm();
    }
    ready = true;
    void preloadInterstitial();
  } catch (error) {
    console.warn("[ads] init failed", error);
  }
}

async function preloadInterstitial() {
  if (!admob) return;
  try {
    await admob.AdMob.prepareInterstitial({ adId: IDS.interstitial, isTesting: TESTING });
  } catch {
    // No fill — try again next round.
  }
}

/** Call when the player leaves a result screen. */
export async function maybeShowInterstitial() {
  if (!ready || !admob) return;
  roundsSinceAd++;
  if (roundsSinceAd < INTERSTITIAL_EVERY || Date.now() - lastInterstitial < INTERSTITIAL_GAP_MS) return;
  try {
    await admob.AdMob.showInterstitial();
    roundsSinceAd = 0;
    lastInterstitial = Date.now();
  } catch {
    // Not loaded yet; ignore.
  }
  void preloadInterstitial();
}

/** Shows a rewarded video; resolves true when the reward was earned. */
export async function showRewarded(): Promise<boolean> {
  if (!ready || !admob) return false;
  const { AdMob, RewardAdPluginEvents } = admob;
  let rewarded = false;
  const handle = await AdMob.addListener(RewardAdPluginEvents.Rewarded, () => { rewarded = true; });
  try {
    await AdMob.prepareRewardVideoAd({ adId: IDS.rewarded, isTesting: TESTING });
    await AdMob.showRewardVideoAd();
  } catch {
    rewarded = false;
  } finally {
    await handle.remove();
  }
  // A rewarded ad counts as the break; don't follow it with an interstitial.
  if (rewarded) {
    roundsSinceAd = 0;
    lastInterstitial = Date.now();
  }
  return rewarded;
}
